import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Button, Typography } from "@mui/material";
import { StyledPdfViewer } from "./pdfViewer.styled";
import { usePanZoom } from "../../hooks/fileEditor/usePanZoom";
import { File } from "../../models/File";

// pdf worker (REQUIRED)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type PdfViewerProps = {
  file: File;
};

export const PdfViewer = ({ file }: PdfViewerProps) => {
  const [numPages, setNumPages] = useState(0);
  const documentRef = useRef<HTMLDivElement | null>(null);
  const { zoom } = usePanZoom(documentRef, "hand");

  useEffect(() => {
    const el = documentRef.current;
    if (!el) return;

    const prevent = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    el.addEventListener("touchstart", prevent, { passive: false });
    el.addEventListener("touchmove", prevent, { passive: false });

    return () => {
      el.removeEventListener("touchstart", prevent);
      el.removeEventListener("touchmove", prevent);
    };
  }, [documentRef]);

  const pdfFile = useMemo(() => {
    const binary = atob(file.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return { data: bytes };
  }, [file.base64]);

  return (
    <StyledPdfViewer>
      <Typography variant="h2">{file.filename}</Typography>
      <Box className="viewport" ref={documentRef}>
        <Document
          className="document"
          file={pdfFile}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          loading={<Typography color="white">Loading PDF…</Typography>}>
          {Array.from(new Array(numPages), (_, i) => (
            <Box className="page" key={i}>
              <Page
                pageNumber={i + 1}
                scale={zoom}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Box>
          ))}
        </Document>
      </Box>
    </StyledPdfViewer>
  );
};
